import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Language } from '@/i18n/types';
import type { AppTranslations } from '@/i18n/app-translations.types';
import {
  appTranslationsEn,
  getCachedLanguage,
  preloadLanguage,
} from '@/i18n/app/loader';

const getLocale = (language: Language): string => {
  switch (language) {
    case 'fr':
      return 'fr-FR';
    case 'nl':
      return 'nl-NL';
    case 'en':
    default:
      return 'en-US';
  }
};

export const useAppTranslations = () => {
  const { language } = useLanguage();

  // Start from the cached dictionary if present, else EN — never undefined, so
  // the ~650 consumers reading `t.a.b.c` without guards can't hit undefined.
  const [translations, setTranslations] = useState<AppTranslations>(
    () => getCachedLanguage(language) ?? appTranslationsEn
  );

  useEffect(() => {
    const cached = getCachedLanguage(language);
    if (cached) {
      setTranslations(cached);
      return;
    }

    // Non-EN language not yet loaded: keep EN on screen, swap when it arrives.
    let active = true;
    preloadLanguage(language).then((loaded) => {
      if (active) setTranslations(loaded);
    });
    return () => {
      active = false;
    };
  }, [language]);

  return {
    t: translations,
    language,
    locale: getLocale(language),
    isEnglish: language === 'en',
    isFrench: language === 'fr',
    isDutch: language === 'nl',
  };
};
